<template>
  <div>
    <div class="search-bar">
      <div class="search-input-wrapper">
        <font-awesome-icon :icon="['fas','magnifying-glass']" class="search-icon"/>
        <input
            type="text"
            v-model="searchTerm"
            placeholder="Search Devices..."
            class="search-input cardBackground"
        />
      </div>
    </div>
    <div class="list">
      <div
          v-for="device in pagedDevices"
          :key="device.id"
          class="list__item clickable"
          @click="showSites(device)"
      >

        <div class="device‐column device‐icon">
          <font-awesome-icon :icon="['fas','mobile-screen-button']" />
        </div>

        <div class="device‐column device‐name">
          {{ device.name }}
        </div>

        <div class="device‐column device‐info‐icon">
          <font-awesome-icon :icon="['fas','info-circle']" />
        </div>
      </div>

      <!-- If there are no devices to show: -->
      <div v-if="pagedDevices.length === 0" class="no-results">
        No devices found
      </div>

    </div>
    <!-- unified pagination control -->
    <pagination-control
      :currentPage="currentPage"
      :pageCount="pageCount"
      :maxVisible="3"
      @update:currentPage="goToPage"
    />
    <BaseModal
        :visible="modalOpen"
        :title="selectedDevice.name + ' Sites'"
        :submit-show="false"
        cancel-label="Close"
        @close="closeModal"
        @cancel="closeModal"
        modal-width="min(90vw, 50rem)"
    >
      <div class="sites-modal-container">
        <div class="sites-header">
          <div class="header-col site-name-col">Site Name</div>
          <div class="header-col duration-col">Duration</div>
          <div class="header-col limit-col">Daily Limit</div>
          <div class="header-col action-col">Action</div>
        </div>
        <div class="sites-list">
          <div v-for="site of selectedDevice.sites" v-bind:key="site.name" class="site-row">
            <div class="site-col site-name-col">
              <span class="site-name">{{site.name}}</span>
            </div>
            <div class="site-col duration-col">
              <span class="duration">{{calculateTime(site.timeSpent)}}</span>
            </div>
            <div class="site-col limit-col">
              <div class="limit-display">
                <span v-if="site.limit">{{site.limit}}</span>
                <span v-else class="no-limit">No limit</span>
                <font-awesome-icon
                  v-if="site.limit"
                  @click="deleteSiteLimit(site)"
                  class="delete-icon"
                  :icon="['fas','trash']"
                />
              </div>
            </div>
            <div class="site-col action-col">
              <button class="secondaryButton limit-button" @click="openLimit(site)">
                {{site.limit ? 'Edit' : 'Set Limit'}}
              </button>
            </div>
          </div>
          <div v-if="selectedDevice.sites && selectedDevice.sites.length === 0" class="no-sites">
            No sites visited yet
          </div>
        </div>
      </div>
    </BaseModal>
    <BaseModal
        :visible="limitOpen"
        title="Set Daily Limit"
        submit-label="Save"
        @close="closeLimit"
        @cancel="closeLimit"
        @submit="limitSubmit"
    >
      <div class="modalContainer">
        <p>Device: <b>{{selectedDevice.name}}</b></p>
        <p>Domain: <b>{{selectedSite.name}}</b></p>
        <p>Hours (0-23)</p>
        <CustomNumberInput v-model="hourLimit" :min="0" :max="23" :step="1"/>
        <p>Minutes (0-59)</p>
        <CustomNumberInput v-model="minuteLimit" :min="0" :max="59" :step="1"/>
      </div>
    </BaseModal>
    <MessageModal :message="appMessage.text" :type="appMessage.type" ref="messageModal" />
    <LoadingPage :is-visible="loading"/>
  </div>
</template>

<script>
import MessageModal from "@/components/MessageModal.vue";
import DeviceAPIService from "@/servicehandlers/DeviceAPIService";
import LoadingPage from "@/components/LoadingPage.vue";
import {useLoginStore} from "@/stores/loginStore";
import BaseModal from "@/components/BaseModal.vue";
import CustomNumberInput from "@/components/CustomNumberInput.vue";
import PaginationControl from "@/components/PaginationControl.vue";
const deviceAPIService = new DeviceAPIService()

export default {
  name: 'SiteManagerPage',
  components: {CustomNumberInput, BaseModal, LoadingPage, MessageModal, PaginationControl},
  data(){
    return {
      appMessage: {
        text: '',
        type: 'error',
      },
      loading: false,
      searchTerm: '',
      devices: [],
      currentPage: 1,
      itemsPerPage: 6,
      selectedDevice: {},
      modalOpen: false,
      selectedSite: {},
      limitOpen: false,
      hourLimit: 0,
      minuteLimit: 0,
    }
  },
  computed:{
    pagedDevices() {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      return this.filteredDevices.slice(startIndex, endIndex);
    },
    pageCount() {
      return Math.ceil(this.filteredDevices.length / this.itemsPerPage);
    },
    filteredDevices() {
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) return this.devices;
      return this.devices.filter((device) =>
          device.name.toLowerCase().includes(term)
      );
    },
    visiblePages() {
      const pages = [];
      const lower = Math.max(1, this.currentPage - 1);
      const upper = Math.min(this.pageCount, this.currentPage + 1);
      for (let p = lower; p <= upper; p++) {
        pages.push(p);
      }
      return pages;
    }
  },
  async mounted(){
    //console.clear()
    // Only 'device' users can access site manager functionality
    if (useLoginStore().getSubscriptionType === 'device') {
      if (useLoginStore().getDeviceOnline) {
        this.loading = true
        await this.getTrackingData()
        this.loading = false
      } else {
        this.appMessage.text = "Device Offline"
        this.appMessage.type = 'info'
        this.$refs.messageModal.open()
      }
    }
    // 'free' users handled by route guards/subscription service
  },
  methods: {
    showUpgradeModal(featureName) {
      this.$router.push({
        name: 'upgrade',
        query: {
          feature: featureName,
          return: this.$route.path
        }
      })
    },
    showError(e){
      this.appMessage.text = 'Error: ' + e
      this.appMessage.type = 'error'
      this.$refs.messageModal.open()
    },
    async getTrackingData(){
      this.devices = []
      try {
        let domains = await deviceAPIService.getDailyDomainLimits(this.$router, useLoginStore())
        //console.log(domains)
        let tracking = await deviceAPIService.getDeviceTrackingData(this.$router, useLoginStore())
        //console.log(tracking)
        for (let mac of Object.keys(tracking)){
          let trackingData = tracking[mac]
          let tempDevice = {
            id: this.devices.length + 1,
            name: trackingData.name || 'Unknown Device',
            sites: [],
            ip: trackingData.ip,
            mac: trackingData.mac,
          }
          for (let site of Object.keys(trackingData.sites)){
            let tempSite = {
              name: site,
              timeSpent: trackingData.sites[site],
              limit: null,
            }
            if (domains.rules.some(data => data.domain === site)) {
              let limit = domains.rules.find(data => data.domain === site)
              if (tempDevice.ip && limit.deviceIp === tempDevice.ip) {
                tempSite.limit = this.calculateTime(limit.timeAllowed, false) + ' daily'
                tempSite.timeAllowed = limit.timeAllowed
              }
            }
            tempDevice.sites.push(tempSite)
          }
          this.devices.push(tempDevice)
        }
      } catch (e) {
        this.showError(e)
      }
    },
    goToPage(page) {
      if (page < 1) page = 1;
      if (page > this.pageCount) page = this.pageCount;
      this.currentPage = page;
    },
    showSites(device){
      this.selectedDevice = device
      this.modalOpen = true
    },
    closeModal(){
      this.modalOpen = false
    },
    calculateTime(totalSeconds, addSeconds = true){
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const hh = String(hours).padStart(2, '0') + 'h';
      const mm = String(minutes).padStart(2, '0') + 'm';
      const ss = String(seconds.toFixed(0)).padStart(2, '0') + 's';

      let final = ''
      if (parseFloat(hh) > 0){
        final += `${hh} `
      }
      if (parseFloat(mm) > 0){
        final += `${mm} `
      }
      if (addSeconds) {
        final += `${ss}`
      }

      return final;
    },
    openLimit(site){
      this.selectedSite = site
      if (site.limit){
        this.hourLimit = Math.floor(site.timeAllowed / 3600)
        this.minuteLimit = (site.timeAllowed - (this.hourLimit * 3600)) / 60
      }
      this.limitOpen = true
    },
    closeLimit(){
      this.selectedSite = {}
      this.limitOpen = false
      this.hourLimit = 0
      this.minuteLimit = 0
    },
    async limitSubmit(){
      let total = ((this.hourLimit * 60) + this.minuteLimit) * 60
      if (isNaN(total) || total <= 0 || total > 86400){
        this.showError('Place enter a time amount between 1 minute and 24 hours.')
        return
      }
      let payload = {
        domain: this.selectedSite.name,
        timeAllowed: total,
        deviceIp: this.selectedDevice.ip,
        macAddress: this.selectedDevice.mac,
      }

      try {
        if (this.selectedDevice.sites.some(data => data.name === payload.domain && data.limit)) {
          await this.deleteSiteLimit(this.selectedSite, false)
        }
        this.loading = true
        await deviceAPIService.createDomainLimitOnDevice(payload, this.$router, useLoginStore())
        await this.getTrackingData()
        this.closeLimit()
        this.loading = false
        this.selectedDevice = this.devices.find(data => data.mac === payload.macAddress)
      } catch (e) {
        this.showError(e)
        this.loading = false
      }
    },
    async deleteSiteLimit(site, reload = true){
      this.selectedSite = site
      let payload = {
        domain: this.selectedSite.name,
        deviceIp: this.selectedDevice.ip,
      }
      try{
        this.loading = true
        await deviceAPIService.deleteDomainLimitOnDevice(payload, this.$router, useLoginStore())
        if (reload) {
          await this.getTrackingData()
          this.loading = false
          this.selectedDevice = this.devices.find(data => data.ip === payload.deviceIp)
        }
      } catch (e) {
        this.showError(e)
      }
    },
  },
}
</script>

<style scoped>
/* Sites Modal Styles */
.sites-modal-container {
  width: 100%;
  overflow: visible;
}

.sites-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1.2fr 1fr;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: var(--color-background-input);
  border-radius: 0.375rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-primary);
}

.sites-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.site-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1.2fr 1fr;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: var(--color-background-input);
  border-radius: 0.375rem;
  align-items: center;
  transition: background 0.2s;
}

.site-row:hover {
  background: var(--color-border-input);
}

.site-col {
  display: flex;
  align-items: center;
  color: var(--text-primary);
  font-size: 0.9rem;
}

.site-name-col {
  overflow: hidden;
}

.site-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  display: block;
}

.duration-col {
  font-family: 'Courier New', monospace;
}

.limit-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.no-limit {
  color: var(--text-secondary);
  font-style: italic;
  font-size: 0.85rem;
}

.delete-icon {
  color: var(--color-red-button);
  cursor: pointer;
  font-size: 0.9rem;
  transition: opacity 0.2s;
}

.delete-icon:hover {
  opacity: 0.7;
}

.limit-button {
  padding: 0.35rem 0.75rem;
  font-size: 0.85rem;
  width: 100%;
  max-width: 8rem;
}

.no-sites {
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
  font-style: italic;
}

/* Responsive Layout */
@media (max-width: 768px) {
  .sites-header,
  .site-row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .header-col {
    display: none;
  }

  .site-col {
    display: grid;
    grid-template-columns: 100px 1fr;
    gap: 0.5rem;
  }

  .site-col::before {
    content: attr(data-label);
    font-weight: 600;
    color: var(--text-secondary);
  }

  .site-name-col::before {
    content: 'Site:';
  }

  .duration-col::before {
    content: 'Duration:';
  }

  .limit-col::before {
    content: 'Limit:';
  }

  .action-col::before {
    content: 'Action:';
  }

  .limit-button {
    max-width: none;
  }
}

/* Device List Styles */
.clickable {
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
}

.clickable:hover {
  background: var(--color-border-input) !important;
  transform: translateX(2px);
}

.clickable:active {
  transform: translateX(0px);
}

.device‐column {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: var(--text-primary);
}

.device‐icon {
  flex: 0 0 2rem;
  justify-content: flex-start;
  font-size: 1.3rem;
}

.device‐name {
  flex: 2;
  justify-content: flex-start;
  font-size: .9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.device‐info‐icon {
  flex: 0 0 2rem;
  justify-content: flex-end;
  font-size: 1.3rem;
  color: var(--color-secondary-button);
  transition: opacity 0.2s;
}
</style>
